import { createApp, h } from "vue";
import { createInertiaApp, Head, Link, router } from "@inertiajs/vue3";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { InertiaProgress } from "@inertiajs/progress";

import { buildRoutes, createRouteHelper, getRoutes } from "@/utils/route";
import NProgress from "nprogress";
import {
    dateTimeHumanize,
    trans,
    convertJsonToFormData,
    loadTypeform,
    validation,
    isMobile
} from "../helpers.js";

import VueDatePicker from "@vuepic/vue-datepicker";

//Table action menu
import TableMenu from "@/Components/Common/TableMenu.vue";
//Button component
import HrmButton from "@/Components/Common/HrmButton.vue";
//Link component
import HrmLink from "@/Components/Common/HrmLink.vue";
import HrmPageHeader from "@/Components/Common/HrmPageHeader.vue";
import PageHeader from "@/Components/Common/PageHeader.vue";
//Table Component
import HrmTableMain from "@/Components/HrmTableMain.vue";
import ChangePage from "@/Components/Common/ChangePage.vue";
import PopperMultiselect from "@/Components/Common/PopperMultiSelect.vue";
import PopperTagsSelect from "@/Components/Common/PopperTagsSelect.vue";

import HrmModalCard from "@/Components/Common/HrmModalCard.vue";
import HrmCounters from "@/Components/Common/HrmCounters.vue";

import StatusToggle from "@/Common/StatusToggle.vue";
import HrmLoader from "@/Common/HrmLoader.vue";

import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.bubble.css";
import "@vueup/vue-quill/dist/vue-quill.snow.css";

import TipTapEditor from "@/Common/TextEditors/TipTapEditor.vue";

import NoRecordFound from "@/Common/NorecordFound.vue";

import SearchBox from "@/Components/Common/SearchBox.vue";
import FooterTable from "@/Components/Common/FooterTable.vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    DialogPanel,
    Listbox,
    ListboxLabel,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    Popover,
    PopoverButton,
    PopoverPanel,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
} from "@headlessui/vue";

//Employee Info Component
import EmployeeInfo from "@/Components/Common/EmployeeInfo.vue";

import Notifications from "@kyvg/vue3-notification";
import ModalCard from "@/Components/Common/ModalCard.vue";
import LoadingIcon from "@/Common/LoadingIcon.vue";

import ColumnSelector from "@/Common/ColumnSelector.vue";
import Shimmer from "@/Common/Shimmer.vue";
import FieldsShimmer from "@/Common/FieldsShimmer.vue";
import QuillTextEditor from "@/Common/TextEditor.vue";
import CreateDesignationComp from "@/Common/CreateDesignationComp.vue";

import GlobalFilter from "@/Common/GlobalFilter/GlobalFilter.vue";

// Form Components
import FormInput from "@/Common/FormComponents/FormInput.vue";
import BaseGlobalInput from "@/Components/Common/Inputs/BaseGlobalInput.vue";

//import { VueReCaptcha } from 'vue-recaptcha-v3'
import tooltip from "@/directives/tooltip.js";
import clickOutside from "@/directives/clickoutside.js";

import { createPinia } from "pinia";
const pinia = createPinia();

import { useAppStore } from "@/store/index";
import { usePermissions, useProjectPermissions } from "@/Mixins/perm";
import AuthenticatedLayout from "@/Layouts/AuthenticatedLayout.vue";
import { getFormPayload } from "../../../Modules/CustomField/Resources/js/Composables/index";


// Global Data Setup
const appName =
    window.document.getElementsByTagName("title")[0]?.innerText || "Laravel";

const appPages = import.meta.glob("../Pages/**/*.vue");
const modulePages = import.meta.glob("../../../Modules/*/Resources/js/Pages/**/*.vue");
const pagesCache = {};

const normalizePageName = (name: string) => {
    return name.split("?")[0].replace('.vue','')
}

export default function DICreateInertiaApp() {
    createInertiaApp({
        title: (title) => `${title} - ${appName}`,

        resolve: async (name) => {
            const cacheKey = normalizePageName(name)

            if (pagesCache[cacheKey]) return Promise.resolve(pagesCache[cacheKey])

            const parts = name.split("::")
            let page: any;

            if (parts.length > 1) {
                const nameVue = parts[1].split(".")[0]

                page = await resolvePageComponent(
                    `../../../Modules/${parts[0]}/Resources/js/Pages/${nameVue}.vue`,
                    modulePages
                )
            } else {
                page = await resolvePageComponent(`../Pages/${name}.vue`, appPages)
            }

            // ⭐ IMPORTANT — layout persist
            // page.default.layout ??= page.default.layout || AuthenticatedLayout

            pagesCache[cacheKey] = page
            return page
        },

        setup({ el, App, props, plugin }) {
            const { auth, routeContext, activeModules }: any =
                props.initialPage.props;
            const routes = buildRoutes(routeContext, activeModules);
            const routeHelper = createRouteHelper(routes);
            const routerListView = getRoutes(routes);

            const vueApp = createApp({
                render: () => h(App, props),
                mounted() {
                    useAppStoreObj.subscribeAppVersionReleaseEvent();
                    const authUserId = auth?.user?.id;
                },
                async created() { },
            });

            vueApp
                .use(plugin)
                .use(pinia)
                .component("InertiaHead", Head)
                .component("InertiaLink", Link)
                .component("VueDatePicker", VueDatePicker)
                .component("Listbox", Listbox)
                .component("ListboxLabel", ListboxLabel)
                .component("ListboxButton", ListboxButton)
                .component("ListboxOptions", ListboxOptions)
                .component("ListboxOption", ListboxOption)
                .component("Combobox", Combobox)
                .component("ComboboxInput", ComboboxInput)
                .component("ComboboxButton", ComboboxButton)
                .component("ComboboxOptions", ComboboxOptions)
                .component("ComboboxOption", ComboboxOption)
                .component("Popover", Popover)
                .component("PopoverButton", PopoverButton)
                .component("PopoverPanel", PopoverPanel)
                .component("TableMenu", TableMenu)
                .component("ModalCard", ModalCard)
                .component("LoadingIcon", LoadingIcon)
                .component("HrmButton", HrmButton)
                .component("HrmLink", HrmLink)
                .component("HrmPageHeader", HrmPageHeader)
                .component("PageHeader", PageHeader)
                .component("SearchBox", SearchBox)
                .component("FooterTable", FooterTable)
                .component("HrmTableMain", HrmTableMain)
                .component("ChangePage", ChangePage)
                .component("HrmModalCard", HrmModalCard)
                .component("HrmCounters", HrmCounters)
                .component("Menu", Menu)
                .component("MenuButton", MenuButton)
                .component("MenuItems", MenuItems)
                .component("MenuItem", MenuItem)
                .component("NoRecordFound", NoRecordFound)
                .component("EmployeeInfo", EmployeeInfo)
                .component("ColumnSelector", ColumnSelector)
                .component("PopperMultiselect", PopperMultiselect)
                .component("QuillEditor", QuillEditor)
                .component("StatusToggle", StatusToggle)
                .component("HrmLoader", HrmLoader)
                .component("Dialog", Dialog)
                .component("DialogOverlay", DialogOverlay)
                .component("DialogTitle", DialogTitle)
                .component("TransitionChild", TransitionChild)
                .component("TransitionRoot", TransitionRoot)
                .component("DialogPanel", DialogPanel)
                .component("PopperTagsSelect", PopperTagsSelect)
                .component("Shimmer", Shimmer)
                .component("FieldsShimmer", FieldsShimmer)
                .component("QuillTextEditor", QuillTextEditor)
                .component("CreateDesignationComp", CreateDesignationComp)
                .component("GlobalFilter", GlobalFilter)
                .component("TipTapEditor", TipTapEditor)
                .component("FormInput", FormInput)
                .component("BaseGlobalInput", BaseGlobalInput)
                .mixin({
                    methods: {
                        // route: routeHelper,
                        dateTimeHumanize,
                        convertJsonToFormData,
                        loadTypeform,
                    },
                })
                .use(Notifications);

            // application.use(store);
            const useAppStoreObj = useAppStore();

            // Global Error handlers
            vueApp.config.errorHandler = (err, instance, info) => {
                console.error("Global Error Captured: =======>", err);
                console.error("Errors In Component:   =======>", instance);
                console.error("Infos In Component:    =======>", info);
            };
            vueApp.config.globalProperties.mediaPath =
                window._app_config_?.AWS_S3;
            vueApp.config.globalProperties.dateFormat =
                window._app_config_?.DATE_FORMAT;

            vueApp.directive("tooltip", tooltip);
            vueApp.directive("click-outside", clickOutside);

            // Global Functions
            const { checkPermission } = usePermissions();
            const { canAccess } = useProjectPermissions();

            // vue template access
            vueApp.config.globalProperties.checkPermission = checkPermission;
            vueApp.config.globalProperties.canAccess = canAccess;
            vueApp.config.globalProperties.trans = trans;
            vueApp.config.globalProperties.route = routeHelper;
            vueApp.config.globalProperties.axios = axios;
            vueApp.config.globalProperties.router = router;
            vueApp.config.globalProperties.validation = validation;
            vueApp.config.globalProperties.isMobile = isMobile;
            vueApp.config.globalProperties.getFormPayload = getFormPayload

            // window access (script)
            window.trans = trans;
            window.route = routeHelper;
            window.axios = axios;
            window.router = router;
            window.checkPermission = checkPermission;
            window.canAccess = canAccess;
            window.validation = validation;
            window.isMobile =  isMobile;
            window.getFormPayload =  getFormPayload;

            // For view router list
            window.routerListView = routerListView;

            vueApp.mount(el);
        },
    });
}

InertiaProgress.init({
    color: "#4B5563",
});

const reqTransform = window.axios.defaults.transformRequest;

window.axios.defaults.transformRequest = [
    ...(Array.isArray(reqTransform) ? reqTransform : [reqTransform]),
    (data, headers) => {
        NProgress.start();
        return data;
    },
];

const resTransform = window.axios.defaults.transformResponse;

window.axios.defaults.transformResponse = [
    ...(Array.isArray(resTransform) ? resTransform : [resTransform]),
    (data) => {
        NProgress.done();
        return data;
    },
];
